import { useState, useEffect, useRef, useCallback } from 'react';

// Hero carousel — 5 real app screenshots. Auto-advance every 4s, pauses on
// interaction (swipe/drag/hover), infinite loop, smooth cross-fade, dot indicators.
// Images are all 900x1757 so the container height stays stable (no layout shift).

const SLIDES = [
  { src: '/landing-hero-advisor.jpg',   alt: 'Fencepost Advisor — Thunder Cactus Ranch daily overview with 75°F weather, priority work, and daily chores' },
  { src: '/landing-hero-garden.jpg',    alt: 'Fencepost Garden — Manage tab showing raised beds for avocado and blueberries, plus the layout planner' },
  { src: '/landing-hero-poultry.jpg',   alt: 'Fencepost Poultry — Manage tab showing the First Run flock of Rhode Island Red layers' },
  { src: '/landing-hero-livestock.jpg', alt: 'Fencepost Livestock — Manage tab with 67 goats, 14 sheep, 3 cattle, and the milk production calendar' },
  { src: '/landing-hero-reports.jpg',   alt: 'Fencepost Reports — Generate tab with Grant Application, Loan, and Annual Operations templates' },
];

const AUTO_MS = 4000;
const INTERACT_PAUSE_MS = 8000; // temporarily pause auto-advance after user interaction

const HeroCarousel = () => {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchStartX = useRef(null);
  const touchStartY = useRef(null);
  const pauseTimer = useRef(null);

  const go = useCallback((next) => {
    setIndex((prev) => (next + SLIDES.length) % SLIDES.length);
  }, []);

  // Auto-advance timer
  useEffect(() => {
    if (paused) return;
    const t = setTimeout(() => go(index + 1), AUTO_MS);
    return () => clearTimeout(t);
  }, [index, paused, go]);

  // Preload the next slide so cross-fade is smooth
  useEffect(() => {
    const nextIdx = (index + 1) % SLIDES.length;
    const img = new Image();
    img.src = SLIDES[nextIdx].src;
  }, [index]);

  const pauseBriefly = () => {
    setPaused(true);
    if (pauseTimer.current) clearTimeout(pauseTimer.current);
    pauseTimer.current = setTimeout(() => setPaused(false), INTERACT_PAUSE_MS);
  };

  const onTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  };
  const onTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    const dy = e.changedTouches[0].clientY - touchStartY.current;
    // Treat as horizontal swipe only when |dx| dominates and crosses threshold
    if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
      pauseBriefly();
      go(dx < 0 ? index + 1 : index - 1);
    }
    touchStartX.current = null;
    touchStartY.current = null;
  };

  const onDotClick = (i) => {
    pauseBriefly();
    setIndex(i);
  };

  return (
    <div
      className="relative animate-slide-up stagger-2"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      data-testid="hero-carousel"
    >
      <div
        className="rounded-2xl overflow-hidden shadow-2xl bg-background border border-border/60 relative"
        style={{ aspectRatio: '900 / 1757' }}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        {SLIDES.map((s, i) => (
          <img
            key={s.src}
            src={s.src}
            alt={s.alt}
            loading={i === 0 ? 'eager' : 'lazy'}
            decoding="async"
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ease-in-out ${i === index ? 'opacity-100' : 'opacity-0'}`}
            style={{ pointerEvents: i === index ? 'auto' : 'none' }}
            data-testid={`hero-slide-${i}`}
          />
        ))}
      </div>

      <div className="flex items-center justify-center gap-2 mt-4" role="tablist" aria-label="App screenshot carousel">
        {SLIDES.map((_, i) => (
          <button
            key={i}
            type="button"
            onClick={() => onDotClick(i)}
            aria-label={`Show slide ${i + 1} of ${SLIDES.length}`}
            aria-selected={i === index}
            role="tab"
            data-testid={`hero-dot-${i}`}
            className={`transition-all rounded-full ${i === index ? 'w-6 h-2 bg-primary' : 'w-2 h-2 bg-muted-foreground/30 hover:bg-muted-foreground/60'}`}
          />
        ))}
      </div>
    </div>
  );
};

export default HeroCarousel;
